import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type SparklineTone = 'brand' | 'accent' | 'warning' | 'danger';

/**
 * `<nb-sparkline>` — tiny trend line for `NbStatCard` (2026-09 styre prototype:
 * 120 × 36, line + soft area, draws in with `nb-draw` and the last point pops).
 *
 * Purely decorative: `aria-hidden="true"`. The value it illustrates MUST be
 * rendered as text next to it (the stat card's value/delta) — never rely on
 * the line to convey the number.
 *
 * @example <nb-sparkline [data]="[2, 3, 3, 5, 4, 6, 8, 7]" tone="warning" />
 */
@Component({
  selector: 'nb-sparkline',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { 'aria-hidden': 'true', '[class]': 'tone()' },
  template: `<svg [attr.width]="w" [attr.height]="h" [attr.viewBox]="'0 0 ' + w + ' ' + h">
    <path class="area" [attr.d]="areaD()" />
    <path class="line" [attr.d]="lineD()" pathLength="1" />
    <circle class="end" [attr.cx]="last().x" [attr.cy]="last().y" r="2.6" />
  </svg>`,
  styleUrl: './sparkline.css',
})
export class NbSparkline {
  /** The series, oldest → newest. Fewer than 2 points renders a flat line. */
  readonly data = input.required<number[]>();
  /** Line/area colour family. */
  readonly tone = input<SparklineTone>('brand');

  protected readonly w = 120;
  protected readonly h = 36;

  private readonly points = computed(() => {
    const d = this.data().length >= 2 ? this.data() : [this.data()[0] ?? 0, this.data()[0] ?? 0];
    const max = Math.max(...d);
    const min = Math.min(...d);
    const rng = max - min || 1;
    return d.map((v, i) => ({
      x: (i / (d.length - 1)) * this.w,
      y: this.h - 4 - ((v - min) / rng) * (this.h - 8),
    }));
  });

  protected readonly lineD = computed(() =>
    this.points()
      .map((p, i) => (i ? 'L' : 'M') + p.x.toFixed(1) + ' ' + p.y.toFixed(1))
      .join(' '),
  );
  protected readonly areaD = computed(() => `${this.lineD()} L${this.w} ${this.h} L0 ${this.h} Z`);
  protected readonly last = computed(() => this.points()[this.points().length - 1]);
}
